import { UnitPrefix } from 'types/GlobalKpiOption'
import { getUnitLabel } from 'utils/unitLabel'

export type TUnitPrefixOption = { value: UnitPrefix; label: string }

// the next prefix is used once a value would need more integer digits than this
export const AUTO_MAX_INTEGER_DIGITS = 4

export const AUTO_SIGNIFICANT_FIGURES = 3

const PREFIX_FACTORS: Record<UnitPrefix, number> = {
  [UnitPrefix.THOUSAND]: 1_000,
  [UnitPrefix.MILLION]: 1_000_000,
  [UnitPrefix.BILLION]: 1_000_000_000
}

/**
 * The smallest prefix that keeps the largest value within
 * AUTO_MAX_INTEGER_DIGITS, or the largest prefix available.
 */
export const resolveAutoPrefix = (
  values: (number | null | undefined)[],
  options: TUnitPrefixOption[]
): UnitPrefix | null => {
  const limit = 10 ** AUTO_MAX_INTEGER_DIGITS
  const reference = values.reduce<number>(
    (max, value) =>
      typeof value === 'number' && Number.isFinite(value)
        ? Math.max(max, Math.abs(value))
        : max,
    0
  )

  if (reference < limit || options.length === 0) {
    return null
  }

  const sorted = [...options].sort(
    (a, b) => PREFIX_FACTORS[a.value] - PREFIX_FACTORS[b.value]
  )
  const fitting = sorted.find(
    (option) => reference / PREFIX_FACTORS[option.value] < limit
  )

  return (fitting ?? sorted[sorted.length - 1]).value
}

/** One prefix and label shared by all values shown together. */
export const getAutoScale = (
  values: (number | null | undefined)[],
  unit: string | null | undefined,
  options: TUnitPrefixOption[] | null | undefined
): { unitPrefix: UnitPrefix | null; unitLabel: string } => {
  const prefixOptions = unit === '%' ? [] : options ?? []
  const unitPrefix = resolveAutoPrefix(values, prefixOptions)

  return {
    unitPrefix,
    unitLabel: getUnitLabel(unit ?? null, {
      selected: unitPrefix,
      options: prefixOptions
    })
  }
}
